import React, { FC, useEffect, useState } from "react";
import { Alert, Button, Col, Modal, ModalBody, Row } from "reactstrap";
import { Icon } from "../../../components/Component";
import { Select } from "antd";
import { DeleteOutlined } from "@ant-design/icons";

const questionTypeOptions = [
  { label: "Short Text(30 Words)", value: "shortText", payloadType: "Short Text" },
  { label: "Long Text(500 Words)", value: "longText", payloadType: "Long Text" },
  { label: "Single Choice", value: "singleChoice", payloadType: "Single Choice" },
  { label: "Dropdown Single Choice", value: "dropdownSingleChoice", payloadType: "Single Choice" },
  { label: "Multiple Choice", value: "multipleChoice", payloadType: "Multiple Choice" },
  { label: "Dropdown multiple Choice", value: "dropdownMultipleChoice", payloadType: "Multiple Choice" },
  { label: "File Upload", value: "fileUpload", payloadType: "File Upload" },
];

const QuestionerModal: FC<any> = ({
  modal,
  closeModal,
  append,
  update,
  formData,
}) => {
  const isEdit = formData?.action === "edit" && formData?.item;
  const editItem = formData?.item || {};

  const [questioner, setQuestioner] = useState(
    isEdit ? editItem.questioner || "" : ""
  );
  const [questionWeightage, setQuestionWeightage] = useState(
    isEdit
      ? String(editItem.questionWeightage || "")
          .replace(/[^0-9.]/g, "")
          .trim()
      : ""
  );
  const [questionType, setQuestionType] = useState(() => {
    if (!isEdit) return "";
    const savedType = editItem.questionType || "";
    // If savedType is already a dropdown value (e.g. "shortText"), use it directly
    const matchByValue = questionTypeOptions.find((opt) => opt.value === savedType);
    if (matchByValue) return savedType;
    // If savedType is a payloadType (e.g. "Short Text") or label, map back to value
    const matchByPayload = questionTypeOptions.find((opt) => opt.payloadType === savedType);
    if (matchByPayload) return matchByPayload.value;
    const matchByLabel = questionTypeOptions.find((opt) => opt.label === savedType);
    return matchByLabel ? matchByLabel.value : savedType;
  });
  const [description, setDescription] = useState(
    isEdit ? editItem.description || "" : ""
  );
  const [choiceOptions, setChoiceOptions] = useState<string[]>(
    isEdit && editItem.choiceOptions?.length > 0 ? editItem.choiceOptions : []
  );
  const [newOption, setNewOption] = useState<string>("");

  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [] as string[],
  });

  // Form validation errors
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (alertInfo.message) {
      const timeoutId = setTimeout(() => {
        setAlertInfo({ type: "", message: "", errors: [] });
      }, 2500);
      return () => clearTimeout(timeoutId);
    }
  }, [alertInfo]);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!questioner.trim()) errors.questioner = "This field is required";
    if (!questionWeightage)
      errors.questionWeightage = "This field is required";
    if (!questionType) errors.questionType = "This field is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const isChoiceBased =
      questionType === "singleChoice" ||
      questionType === "multipleChoice" ||
      questionType === "dropdownSingleChoice" ||
      questionType === "dropdownMultipleChoice";

    if (isChoiceBased && choiceOptions.length === 0) {
      setAlertInfo({
        type: "error",
        message: "Please add at least one choice option",
        errors: [],
      });
      return;
    }

    const questionTypeForPayload =
      questionTypeOptions.find((opt) => opt.value === questionType)?.payloadType ||
      questionType;

    const questionData: any = {
      questioner,
      questionWeightage,
      questionType: questionTypeForPayload,
      description,
    };

    if (isChoiceBased || choiceOptions.length > 0) {
      questionData.choiceOptions = choiceOptions;
    }

    if (formData.action === "add") {
      append(questionData);
    } else if (formData.action === "edit" || formData.action === "update") {
      // Preserve existing fields (like id) from the original item
      update(formData.index, { ...editItem, ...questionData });
    }
    closeModal();
  };

  const isChoiceBased =
    questionType === "singleChoice" ||
    questionType === "multipleChoice" ||
    questionType === "dropdownSingleChoice" ||
    questionType === "dropdownMultipleChoice";

  return (
    <Modal isOpen={modal} toggle={closeModal} size="xl" backdrop="static">
      <ModalBody>
        <h5 className="title">
          {formData.action == "add" || formData.index == null
            ? "Add New Question"
            : "Update Question"}
        </h5>
        <div style={{ marginTop: "15px" }}></div>
        {alertInfo.message && (
          <div className="mb-3 mt-1">
            <Alert
              color={alertInfo.type === "error" ? "danger" : "success"}
              className="alert-icon"
            >
              <strong>{alertInfo.message}</strong>
              <Icon
                name={
                  alertInfo.type === "error" ? "alert-circle" : "check-circle"
                }
              />
              {alertInfo.errors.length > 0 ? (
                <ul>
                  {alertInfo.errors.map((err: any, index: any) => (
                    <li key={index}>{err}</li>
                  ))}
                </ul>
              ) : (
                ""
              )}
            </Alert>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <Row>
            <Col lg="6">
              <div className="form-group">
                <label className="form-label" htmlFor="questioner">
                  Ask Question<span className="mandatory">*</span>
                </label>
                <div className="form-control-wrap">
                  <input
                    type="text"
                    id="modal-questioner"
                    className="form-control"
                    placeholder="Questioner"
                    value={questioner}
                    onChange={(e) => setQuestioner(e.target.value)}
                  />
                  {formErrors.questioner && (
                    <p className="mandatory">{formErrors.questioner}</p>
                  )}
                </div>
              </div>
            </Col>
            <Col lg="6">
              <div className="form-group">
                <label className="form-label" htmlFor="questionWeightage">
                  Question Weightage<span className="mandatory">*</span>
                </label>
                <div className="form-control-wrap">
                  <input
                    type="number"
                    id="modal-questionWeightage"
                    className="form-control"
                    placeholder="Enter Question Weightage"
                    value={questionWeightage}
                    step="any"
                    onChange={(e) => setQuestionWeightage(e.target.value)}
                  />
                  {formErrors.questionWeightage && (
                    <p className="mandatory">{formErrors.questionWeightage}</p>
                  )}
                </div>
              </div>
            </Col>
          </Row>
          <div style={{ marginTop: "15px" }}></div>
          <Row>
            <Col lg="6">
              <div className="form-group">
                <label className="form-label" htmlFor="questionType">
                  Type<span className="mandatory">*</span>
                </label>
                <div className="form-control-wrap">
                  <Select
                    id="modal-questionType"
                    options={questionTypeOptions}
                    value={questionType || undefined}
                    onChange={(val: string) => setQuestionType(val)}
                    style={{ width: "100%", height: "38px" }}
                    placeholder="Select Type"
                    showSearch
                    filterOption={(input: any, option: any) =>
                      option?.label
                        ?.toLowerCase()
                        ?.indexOf(input?.toLowerCase()) >= 0
                    }
                  />
                  {formErrors.questionType && (
                    <p className="mandatory">{formErrors.questionType}</p>
                  )}
                </div>
              </div>
            </Col>
            <Col lg="6">
              {isChoiceBased && (
                <div className="mb-4">
                  <label className="form-label">
                    Options {<span className="mandatory">*</span>}
                  </label>
                  <div className="border rounded p-3">
                    {choiceOptions.length > 0 ? (
                      <ul className="list-group mb-3">
                        {choiceOptions.map((option, index) => (
                          <li
                            key={index}
                            className="list-group-item d-flex justify-content-between align-items-center"
                          >
                            <span>{option}</span>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              type="button"
                              onClick={() =>
                                setChoiceOptions(
                                  choiceOptions.filter((_, i) => i !== index)
                                )
                              }
                            >
                              <DeleteOutlined />
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-muted mb-3">
                        No options added yet
                      </div>
                    )}

                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Add new option"
                        value={newOption}
                        onChange={(e) => setNewOption(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter" && newOption.trim()) {
                            e.preventDefault();
                            setChoiceOptions([
                              ...choiceOptions,
                              newOption.trim(),
                            ]);
                            setNewOption("");
                          }
                        }}
                      />
                      <button
                        className="btn btn-primary"
                        type="button"
                        disabled={!newOption.trim()}
                        onClick={() => {
                          setChoiceOptions([
                            ...choiceOptions,
                            newOption.trim(),
                          ]);
                          setNewOption("");
                        }}
                      >
                        Add
                      </button>
                    </div>

                    {choiceOptions.length > 0 && (
                      <div className="mt-2">
                        <small className="text-muted">
                          {choiceOptions.length} option
                          {choiceOptions.length !== 1 ? "s" : ""} added
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Col>
          </Row>
          <div style={{ marginTop: "15px" }}></div>
          <Col lg="12">
            <div className="form-group">
              <label className="form-label" htmlFor="description">
                Description
              </label>
              <textarea
                id="modal-description"
                className="form-control"
                placeholder="Description"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </Col>
          <Row className="mt-4">
            <Col lg="12">
              <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                {formData.action === "edit" && formData.index != null && (
                  <li>
                    <Button
                      className="btn btn-primary btn-md"
                      type="submit"
                      color="primary"
                    >
                      Update
                    </Button>
                  </li>
                )}
                {(formData.action == "add" || formData.index == null) && (
                  <li>
                    <Button
                      className="btn btn-primary btn-md"
                      type="submit"
                      color="primary"
                    >
                      Save
                    </Button>
                  </li>
                )}
                <li>
                  <a
                    href="#cancel"
                    onClick={(ev) => {
                      ev.preventDefault();
                      closeModal();
                    }}
                    className="link link-light"
                  >
                    Cancel
                  </a>
                </li>
              </ul>
            </Col>
          </Row>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default QuestionerModal;
